import { config } from '../config';

/**
 * Google Ads conversion tracking.
 *
 * The Google tag (gtag.js) itself is injected into `<head>` at build time by
 * the `google-ads-tag` plugin in `vite.config.ts`, and only when
 * `VITE_GOOGLE_ADS_ID` is set. Everything here is a no-op when the tag is
 * absent, so dev/test builds never report conversions.
 *
 * Two conversions are defined in Google Ads for CPAx Academy:
 * - `individual_signup`   — an individual account was just created (fired once per user)
 * - `firm_sales_inquiry`  — the "Talk to Our Sales Team" form was submitted
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Conversion labels from the Google Ads account (`AW-<id>/<label>`). */
export const GOOGLE_ADS_CONVERSIONS = {
  individual_signup: '-PvVCKKGhacdEIau9LhE',
  firm_sales_inquiry: 'rj3cCKWGhacdEIau9LhE',
} as const;

export type GoogleAdsConversion = keyof typeof GOOGLE_ADS_CONVERSIONS;

const CONVERSION_STORAGE_PREFIX = 'gads_conv:';

function safeStorage(storage: () => Storage): Storage | null {
  try {
    return storage();
  } catch {
    return null; // Storage can throw in private/blocked contexts
  }
}

/**
 * Report a conversion to Google Ads.
 *
 * @param conversion Which conversion fired.
 * @param dedupeKey  When given, the conversion is reported at most once for this
 *                   key on this browser (persisted in localStorage). Use it for
 *                   one-time events like account creation so re-renders, reloads
 *                   and returning users never re-fire it.
 * @returns true if the event was sent to gtag.
 */
export function trackAdsConversion(conversion: GoogleAdsConversion, dedupeKey?: string): boolean {
  const adsId = config.googleAds.id;
  if (!adsId || typeof window === 'undefined' || typeof window.gtag !== 'function') return false;

  const storage = dedupeKey ? safeStorage(() => window.localStorage) : null;
  const storageKey = dedupeKey ? `${CONVERSION_STORAGE_PREFIX}${conversion}:${dedupeKey}` : null;
  if (storage && storageKey && storage.getItem(storageKey)) return false;

  window.gtag('event', 'conversion', { send_to: `${adsId}/${GOOGLE_ADS_CONVERSIONS[conversion]}` });
  if (storage && storageKey) storage.setItem(storageKey, '1');
  return true;
}

// ---------------------------------------------------------------------------
// Ad-click / campaign parameter preservation
// ---------------------------------------------------------------------------

/**
 * Query parameters that identify an ad click or campaign. gtag.js stores the
 * click id in a first-party cookie when it sees it in the page URL, so these
 * must survive the app's client-side redirects (home → login, sign-up → plans,
 * etc.) until the tag has had a chance to run.
 */
export const MARKETING_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

const MARKETING_STORAGE_KEY = 'marketing_params';

function marketingParamsFrom(search: string): URLSearchParams {
  const source = new URLSearchParams(search);
  const found = new URLSearchParams();
  for (const name of MARKETING_PARAMS) {
    const value = source.get(name);
    if (value) found.set(name, value);
  }
  return found;
}

/**
 * Stash any marketing params from the landing URL for the rest of the session.
 * Call once at app start, before the router gets a chance to redirect.
 */
export function rememberMarketingParams(search: string = window.location.search): void {
  const found = marketingParamsFrom(search);
  if ([...found.keys()].length === 0) return;
  safeStorage(() => window.sessionStorage)?.setItem(MARKETING_STORAGE_KEY, found.toString());
}

/** Marketing params from the current URL, falling back to the ones stashed at landing. */
export function currentMarketingParams(search: string = window.location.search): URLSearchParams {
  const fromUrl = marketingParamsFrom(search);
  if ([...fromUrl.keys()].length > 0) return fromUrl;
  const stashed = safeStorage(() => window.sessionStorage)?.getItem(MARKETING_STORAGE_KEY);
  return new URLSearchParams(stashed ?? '');
}

/**
 * Append the current marketing params to an in-app path so a redirect keeps
 * the gclid / UTM values in the address bar. Params already present on `path`
 * win. Returns `path` unchanged when there is nothing to carry.
 */
export function withMarketingParams(path: string, search: string = window.location.search): string {
  const params = currentMarketingParams(search);
  if ([...params.keys()].length === 0) return path;

  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const beforeHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = beforeHash.indexOf('?');
  const pathname = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const existing = new URLSearchParams(queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : '');

  for (const [name, value] of params) {
    if (!existing.has(name)) existing.set(name, value);
  }
  const query = existing.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}
